import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, RefreshCw } from 'lucide-react';
import { apiCall } from './_shared';

const ROLES = ['super_admin', 'admin', 'manager', 'reception', 'accountant'];
const TONE = { ok: 'bg-emerald-50 text-emerald-800 border-emerald-200', amber: 'bg-amber-100 text-amber-900 border-amber-300', red: 'bg-red-600 text-white border-red-700 animate-pulse-slow' };

const Issue = ({ i }) => (
  <li className="border-t pt-2 first:border-0 first:pt-0" data-testid={`channel-issue-${i.id}`}>
    <p className={`text-xs font-bold ${i.level === 'red' ? 'text-red-700' : 'text-amber-800'}`}>{i.title}</p>
    <p className="text-xs text-slate-700">{i.detail}</p>
    <p className="text-[11px] text-slate-500 mt-0.5">→ {i.action}</p>
  </li>
);

export const ChannelHealthBadge = ({ userRole, onOpenTab }) => {
  const [h, setH] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const allowed = ROLES.includes(userRole);
  useEffect(() => {
    if (!allowed) return undefined;
    const load = () => apiCall('/api/admin/rates/channel-health').then(setH).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [allowed]);
  if (!allowed || !h) return null;
  const refresh = async () => { setBusy(true); try { setH(await apiCall('/api/admin/rates/channel-health?refresh=true')); } catch { /* keep */ } finally { setBusy(false); } };
  const issues = (h.issues || []).map((i) => <Issue key={i.id} i={i} />);
  const notes = (h.notes || []).map((n) => <li key={n} className="text-[11px] text-slate-500">{n}</li>);
  const label = h.level === 'ok' ? 'Channels OK' : `${h.issues.length} channel ${h.issues.length === 1 ? 'issue' : 'issues'}`;
  const bc = h.bookingcom || {};
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full px-2.5 py-1 ${TONE[h.level] || TONE.ok}`} title="Beds24 / Booking.com health" data-testid="channel-health-badge">
        {h.level === 'ok' ? <Activity className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />} <span className="hidden sm:inline">{label}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border rounded-xl shadow-xl p-3 z-50 text-left" data-testid="channel-health-popover">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-slate-900">Channel health</p>
            <button type="button" onClick={refresh} disabled={busy} className="text-[11px] text-slate-500 inline-flex items-center gap-1 hover:text-slate-900" data-testid="channel-health-refresh"><RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Check now</button>
          </div>
          {issues.length ? <ul className="space-y-2">{issues}</ul> : <p className="text-xs text-emerald-700" data-testid="channel-health-ok">Beds24 answering, local copy fresh{bc.in_season ? `, last Booking.com reservation ${bc.silence_days} d ago (normal: every ${bc.typical_gap_days} d)` : ''}.</p>}
          {notes.length > 0 && <ul className="mt-2 space-y-0.5">{notes}</ul>}
          <p className="text-[10px] text-slate-400 mt-2">Booking.com status is inferred from booking flow (no extranet API); checked every 30 min · {String(h.checked_at || '').slice(11, 16)} UTC. {onOpenTab && <button type="button" onClick={() => { setOpen(false); onOpenTab('calendar'); }} className="underline" data-testid="channel-health-open-calendar">Open calendar</button>}</p>
        </div>
      )}
    </div>
  );
};
